import { Injectable } from '@angular/core';
import { Company } from '../company/company.model';
import { Customer } from '../customer/customer.model';
import { BaseMasterData } from 'src/app/models/common/base-master-data.model';
import { VehicleType } from 'src/app/fleet/vehicle/params/vehicle-type';
import { VehicleMake } from 'src/app/fleet/vehicle/params/vehicle-make';
import { UOM } from 'src/app/models/meta-params/uom';
import { Country } from 'src/app/models/common/country.model';
import { Weight } from 'src/app/models/meta-params/weight';
import { Length } from 'src/app/models/meta-params/length';
import { PayloadType } from 'src/app/models/meta-params/payload-type';
import { TripType } from 'src/app/models/vehicle/trip-type';
import { Location } from 'src/app/location/location.model';
import { Contracts } from "../contracts/models/contracts.model";
import { TransportationAgency } from "../company/transportation-agency/transportation-agency.model";
import {FreightForwarder} from "../company/freight-forwarder/freight-forwarder.model";
import {CustomsClearanceHeader} from "../clearance/models/customs-clearance-header.model";
import {CustomsClearanceDetail} from "../clearance/models/customs-clearance.detail";
import { TenantAccessSummary, TenantScope } from "../company/tenant-access.model";


const ACCESS_TOKEN_KEY = 'auth-access-token';
const ACCESS_TOKEN_EXPIRY = 'auth-access-expiry';
const REFRESH_TOKEN_KEY = 'auth-refresh-token';
const BROWSER_SESSION_ID_KEY = 'auth-browser-session-id';
const AUTH_PERSISTENCE_KEY = 'auth-persistence';
const USER_KEY = 'auth-user';
const CUSTOMER_KEY = 'auth-customer';
const COMPANY_KEY = 'auth-company';
const TENANT_ACCESS_SUMMARY_KEY = 'tenant-access-summary';
const ACTIVE_TENANT_SCOPE_KEY = 'active-tenant-scope';
const LEGACY_SHIPMENT_DETAIL_KEY = 'order-query';
const LEGACY_DELIVERY_DETAIL_KEY = 'order-delivery';
const SHIPMENT_UPLOAD_CONTEXT_KEY = 'shipment-upload-context';
const VEHICLE_TYPES_KEY = 'vehicle-types';
const VEHICLE_MAKES_KEY = 'vehicle-makes';
const UOMS_KEY = 'uoms';
const COUNTRY_KEY = 'countries';
const WEIGHT_KEY = 'weight-units';
const LENGTH_KEY = 'length-units';
const PAYLOAD_TYPE_KEY = 'payload-types';
const TRIP_TYPE_KEY = 'trip-types';
const LOCATION_LIST_KEY = 'location-list';
const CURRENT_CONTRACT_KEY = 'current-contract';
const CLEARANCE_HEADER_KEY = 'clearance-header';
const CLEARANCE_DETAIL_KEY = 'clearance-detail';
const TRANSPORTATION_AGENCY_KEY = 'transportationAgency';
const FREIGHT_FORWARDER_KEY = 'freightForwarder';

type AuthPersistenceMode = 'session' | 'local';

export interface ShipmentUploadContext {
    shipmentId: number;
    deliveryId?: number;
}

const PERSISTENT_KEYS = [
    ACCESS_TOKEN_KEY,
    ACCESS_TOKEN_EXPIRY,
    REFRESH_TOKEN_KEY,
    BROWSER_SESSION_ID_KEY,
    USER_KEY,
    CUSTOMER_KEY,
    COMPANY_KEY,
    TENANT_ACCESS_SUMMARY_KEY,
    ACTIVE_TENANT_SCOPE_KEY,
    VEHICLE_TYPES_KEY,
    VEHICLE_MAKES_KEY,
    UOMS_KEY,
    COUNTRY_KEY,
    WEIGHT_KEY,
    LENGTH_KEY,
    PAYLOAD_TYPE_KEY,
    TRIP_TYPE_KEY,
    TRANSPORTATION_AGENCY_KEY,
    FREIGHT_FORWARDER_KEY
];

const SESSION_ONLY_KEYS = [
    SHIPMENT_UPLOAD_CONTEXT_KEY,
    LOCATION_LIST_KEY,
    CURRENT_CONTRACT_KEY,
    CLEARANCE_HEADER_KEY,
    CLEARANCE_DETAIL_KEY
];

const MANAGED_KEYS = [...PERSISTENT_KEYS, ...SESSION_ONLY_KEYS];


@Injectable({
    providedIn: 'root'
})
export class SessionStorageService {

    constructor() {
        // Route data is always loaded from its canonical API. Clear obsolete page
        // snapshots so a session upgraded from the legacy flow cannot reuse them.
        window.sessionStorage.removeItem(LEGACY_SHIPMENT_DETAIL_KEY);
        window.sessionStorage.removeItem(LEGACY_DELIVERY_DETAIL_KEY);
    }

    signOut(): void {
        window.sessionStorage.clear();
        MANAGED_KEYS.forEach(key => window.localStorage.removeItem(key));
        window.localStorage.removeItem(AUTH_PERSISTENCE_KEY);
    }

    public setRememberMe(enabled: boolean): void {
        const valuesToPreserve = new Map<string, string>();
        PERSISTENT_KEYS.forEach(key => {
            const value = this.readPersistedValue(key);
            if (value !== null) {
                valuesToPreserve.set(key, value);
            }
        });

        if (enabled) {
            window.localStorage.setItem(AUTH_PERSISTENCE_KEY, 'local');
        } else {
            window.localStorage.removeItem(AUTH_PERSISTENCE_KEY);
        }

        valuesToPreserve.forEach((value, key) => this.writePersistedValue(key, value));
    }

    public isRememberMeEnabled(): boolean {
        return this.persistenceMode() === 'local';
    }

    public saveAccessToken(token: string, expiry: number): void {
        this.writePersistedValue(ACCESS_TOKEN_KEY, token);
        this.writePersistedValue(ACCESS_TOKEN_EXPIRY, expiry.toString());
    }

    public getAccessToken(): string | null {
        return this.readPersistedValue(ACCESS_TOKEN_KEY);
    }

    public getAccessExpiry(): number | null {
        const expiryValue = this.readPersistedValue(ACCESS_TOKEN_EXPIRY);
        if (!expiryValue) {
            return null;
        }
        let tokenCreationDate = Number.parseInt(expiryValue, 10);
        if (Number.isNaN(tokenCreationDate)) {
            return null;
        }
        tokenCreationDate += 3600000;
        return tokenCreationDate;
    }

    public saveRefreshToken(token: string): void {
        this.writePersistedValue(REFRESH_TOKEN_KEY, token);
    }

    public getRefreshToken(): string | null {
        return this.readPersistedValue(REFRESH_TOKEN_KEY);
    }

    public ensureBrowserSessionId(): string {
        let browserSessionId = this.readPersistedValue(BROWSER_SESSION_ID_KEY);
        if (!browserSessionId) {
            browserSessionId = this.createBrowserSessionId();
            this.writePersistedValue(BROWSER_SESSION_ID_KEY, browserSessionId);
        }
        return browserSessionId;
    }

    public getBrowserSessionId(): string | null {
        return this.readPersistedValue(BROWSER_SESSION_ID_KEY);
    }

    public resetBrowserSessionId(): string {
        this.removePersistedValue(BROWSER_SESSION_ID_KEY);
        return this.ensureBrowserSessionId();
    }

    private createBrowserSessionId(): string {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return crypto.randomUUID();
        }
        return 'session-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
    }

    public saveUser(user: any): void {
        this.writePersistedJson(USER_KEY, user);
    }

    public saveCustomer(customer: Customer): void {
        this.writePersistedJson(CUSTOMER_KEY, customer);
        if (customer.company !== null) {
            this.writePersistedJson(COMPANY_KEY, customer.company);
        }
    }

    public getUser(): any {
        const user = this.readPersistedValue(USER_KEY);
        if (user) {
            return JSON.parse(user);
        }
        return null;
    }

    public getCustomer(): Customer | undefined {
        const customer = this.readPersistedValue(CUSTOMER_KEY);
        if (customer) {
            return JSON.parse(customer);
        }
        return undefined;
    }

    public getCompany(): Company | undefined{
        const company = this.readPersistedValue(COMPANY_KEY);
        if (company) {
            return JSON.parse(company);
        }
        return undefined;
    }

    public saveCompany(company: Company): void {
        this.writePersistedJson(COMPANY_KEY, company);
    }

    public saveTenantAccessSummary(summary: TenantAccessSummary): void {
        this.writePersistedJson(TENANT_ACCESS_SUMMARY_KEY, summary);
    }

    public getTenantAccessSummary(): TenantAccessSummary | undefined {
        const summary = this.readPersistedValue(TENANT_ACCESS_SUMMARY_KEY);
        return summary ? JSON.parse(summary) : undefined;
    }

    public saveActiveTenantScope(scope: TenantScope): void {
        this.writePersistedJson(ACTIVE_TENANT_SCOPE_KEY, scope);
    }

    public getActiveTenantScope(): TenantScope | undefined {
        const scope = this.readPersistedValue(ACTIVE_TENANT_SCOPE_KEY);
        return scope ? JSON.parse(scope) : undefined;
    }

    public clearActiveTenantScope(): void {
        this.removePersistedValue(ACTIVE_TENANT_SCOPE_KEY);
    }

    /**
     * Temporary context used only by the legacy shipment-file-upload dialog.
     * Route views must load their data from the URL and must not read this value.
     */
    public setShipmentUploadContext(shipmentId: number, deliveryId?: number): void {
        const context: ShipmentUploadContext = { shipmentId };
        if (deliveryId != null) {
            context.deliveryId = deliveryId;
        }
        window.sessionStorage.setItem(SHIPMENT_UPLOAD_CONTEXT_KEY, JSON.stringify(context));
    }

    public getShipmentUploadContext(): ShipmentUploadContext | undefined {
        const rawContext = window.sessionStorage.getItem(SHIPMENT_UPLOAD_CONTEXT_KEY);
        if (!rawContext) {
            return undefined;
        }

        try {
            const context = JSON.parse(rawContext) as ShipmentUploadContext;
            if (!Number.isFinite(Number(context?.shipmentId)) || Number(context.shipmentId) <= 0) {
                this.clearShipmentUploadContext();
                return undefined;
            }
            if (context.deliveryId != null && (!Number.isFinite(Number(context.deliveryId)) || Number(context.deliveryId) <= 0)) {
                this.clearShipmentUploadContext();
                return undefined;
            }
            return {
                shipmentId: Number(context.shipmentId),
                ...(context.deliveryId != null ? { deliveryId: Number(context.deliveryId) } : {})
            };
        } catch {
            this.clearShipmentUploadContext();
            return undefined;
        }
    }

    public clearShipmentUploadContext(): void {
        window.sessionStorage.removeItem(SHIPMENT_UPLOAD_CONTEXT_KEY);
    }

    //CustomsClearanceHeader
    public setCurrentClearanceHeader(clearanceHeader: CustomsClearanceHeader) {
        window.sessionStorage.removeItem(CLEARANCE_HEADER_KEY);
        window.sessionStorage.setItem(CLEARANCE_HEADER_KEY, JSON.stringify(clearanceHeader));
    }

    public getCurrentClearanceHeader(): CustomsClearanceHeader | undefined {
        const clearanceHeader = window.sessionStorage.getItem(CLEARANCE_HEADER_KEY);
        if (clearanceHeader) {
            return JSON.parse(clearanceHeader);
        }
        return undefined;
    }

    //CustomsClearanceDetail
    public setCurrentClearanceDetail(clearanceDetail: CustomsClearanceDetail) {
        window.sessionStorage.removeItem(CLEARANCE_DETAIL_KEY);
        window.sessionStorage.setItem(CLEARANCE_DETAIL_KEY, JSON.stringify(clearanceDetail));
    }

    public getCurrentClearanceDetail(): CustomsClearanceDetail | undefined {
        const clearanceDetail = window.sessionStorage.getItem(CLEARANCE_DETAIL_KEY);
        if (clearanceDetail) {
            return JSON.parse(clearanceDetail);
        }
        return undefined;
    }

    public saveBaseMasterData(baseMasterData: BaseMasterData) {
        this.writePersistedJson(VEHICLE_TYPES_KEY, baseMasterData.vehicleTypes);
        this.writePersistedJson(VEHICLE_MAKES_KEY, baseMasterData.vehicleMakes);
        this.writePersistedJson(UOMS_KEY, baseMasterData.uomList);
        this.writePersistedJson(COUNTRY_KEY, baseMasterData.countryList);
        this.writePersistedJson(WEIGHT_KEY, baseMasterData.weightUnits);
        this.writePersistedJson(LENGTH_KEY, baseMasterData.lengthUnits);
        this.writePersistedJson(PAYLOAD_TYPE_KEY, baseMasterData.payloadTypes);
        this.writePersistedJson(TRIP_TYPE_KEY, baseMasterData.tripTypes);
    }

    public getVehicleTypes(): VehicleType[] | undefined{
        const vehicleTypes = this.readPersistedValue(VEHICLE_TYPES_KEY);
        if (vehicleTypes) {
            return JSON.parse(vehicleTypes);
        }
        return undefined;
    }

    public getVehicleMakes(): VehicleMake[] | undefined{
        const vehicleMakes = this.readPersistedValue(VEHICLE_MAKES_KEY);
        if (vehicleMakes) {
            return JSON.parse(vehicleMakes);
        }
        return undefined;
    }

    public getUOMList(): UOM[] | undefined{
        const uomList = this.readPersistedValue(UOMS_KEY);
        if (uomList) {
            return JSON.parse(uomList);
        }
        return undefined;
    }

    public getCountryList(): Country[] | undefined{
        const countryList = this.readPersistedValue(COUNTRY_KEY);
        if (countryList) {
            return JSON.parse(countryList);
        }
        return undefined;
    }

    public getWeightUnits(): Weight[] | undefined{
        const weightUnitList = this.readPersistedValue(WEIGHT_KEY);
        if (weightUnitList) {
            return JSON.parse(weightUnitList);
        }
        return undefined;
    }

    public getLengthUnits(): Length[] | undefined{
        const lengthUnits = this.readPersistedValue(LENGTH_KEY);
        if (lengthUnits) {
            return JSON.parse(lengthUnits);
        }
        return undefined;
    }

    public getPayloadTypes(): PayloadType[] | undefined{
        const payloadTypeList = this.readPersistedValue(PAYLOAD_TYPE_KEY);
        if (payloadTypeList) {
            return JSON.parse(payloadTypeList);
        }
        return undefined;
    }

    public getTripTypes(): TripType[] | undefined{
        const tripTypeList = this.readPersistedValue(TRIP_TYPE_KEY);
        if (tripTypeList) {
            return JSON.parse(tripTypeList);
        }
        return undefined;
    }

    setCurrentContract(contract: Contracts): void {
        window.sessionStorage.removeItem(CURRENT_CONTRACT_KEY);
        window.sessionStorage.setItem(CURRENT_CONTRACT_KEY, JSON.stringify(contract));
    }

    getCurrentContract(): Contracts | undefined {
        const contract = window.sessionStorage.getItem(CURRENT_CONTRACT_KEY);
        if (contract) {
            return JSON.parse(contract);
        }
        return undefined;
    }

    saveTransportationAgency(agency: TransportationAgency): void {
        this.writePersistedJson(TRANSPORTATION_AGENCY_KEY, agency);
    }

    getTransportationAgency(): TransportationAgency | null {
        const agencyData = this.readPersistedValue(TRANSPORTATION_AGENCY_KEY);
        return agencyData ? JSON.parse(agencyData) : null;
    }

    saveFreightForwarder(freightForwarder: FreightForwarder): void {
        this.writePersistedJson(FREIGHT_FORWARDER_KEY, freightForwarder);
    }

    getFreightForwarder(): FreightForwarder | null {
        const freightForwarderData = this.readPersistedValue(FREIGHT_FORWARDER_KEY);
        return freightForwarderData ? JSON.parse(freightForwarderData) : null;
    }

    private persistenceMode(): AuthPersistenceMode {
        return window.localStorage.getItem(AUTH_PERSISTENCE_KEY) === 'local' ? 'local' : 'session';
    }

    private persistedStorage(): Storage {
        return this.persistenceMode() === 'local' ? window.localStorage : window.sessionStorage;
    }

    private fallbackPersistedStorage(): Storage {
        return this.persistenceMode() === 'local' ? window.sessionStorage : window.localStorage;
    }

    private readPersistedValue(key: string): string | null {
        const preferredValue = this.persistedStorage().getItem(key);
        if (preferredValue !== null) {
            return preferredValue;
        }
        return this.fallbackPersistedStorage().getItem(key);
    }

    private writePersistedValue(key: string, value: string): void {
        this.removePersistedValue(key);
        this.persistedStorage().setItem(key, value);
    }

    private writePersistedJson(key: string, value: unknown): void {
        this.writePersistedValue(key, JSON.stringify(value));
    }

    private removePersistedValue(key: string): void {
        window.sessionStorage.removeItem(key);
        window.localStorage.removeItem(key);
    }

}
